import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Volume2, Mic, Palette, Languages, AudioLines, UserCircle2, Upload, X, Smartphone, RefreshCw } from "lucide-react";
import { useLang } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEME_DOTS = { dark: "#49f5e8", light: "#f1f5f9", cyber: "#ff4bd8" };
const VOICE_DOTS = { female: "#ff4bd8", male: "#49f5e8", neutral: "#fcee0a" };

const Row = ({ active, dot, label, onClick, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors ${
            active ? "border-[#fcee0a]/60 bg-[#fcee0a]/8 text-[#fcee0a]" : "border-[#212b45] text-slate-300 hover:border-[#49f5e8]/40"
        }`}
    >
        <span className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ background: dot }} />
        <span className="text-sm">{label}</span>
        {active && <Check size={14} className="ml-auto" />}
    </button>
);

export default function SettingsPage() {
    const { t, lang, setLang, theme, setTheme, persona, setPersona, speak, setSpeak, voicePref, setVoicePref, logoChar, setLogoChar, userAvatar, setUserAvatar, user, logout, applyRemoteState } = useLang();
    const [syncStatus, setSyncStatus] = useState("");
    const [syncing, setSyncing] = useState(false);
    const fileRef = useRef(null);

    const syncNow = async () => {
        if (!user || syncing) return;
        setSyncing(true);
        setSyncStatus("");
        try {
            const history = await fetch(`${API}/chat/history/${localStorage.getItem("neon_session")}`).then((r) => r.json()).catch(() => ({ messages: [] }));
            await fetch(`${API}/user/state`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    settings: { lang, theme, persona, speak, voicePref, logoChar, userAvatar },
                    history: history.messages?.slice(-100) || [],
                }),
            });
            setSyncStatus("✓");
            setTimeout(() => setSyncStatus(""), 2500);
        } finally {
            setSyncing(false);
        }
    };

    const onAvatarFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const c = document.createElement("canvas");
                const S = 160;
                c.width = S;
                c.height = S;
                const ctx = c.getContext("2d");
                const side = Math.min(img.width, img.height);
                ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, S, S);
                const data = c.toDataURL("image/jpeg", 0.85);
                setUserAvatar(data);
                localStorage.setItem("neon_user_avatar", data);
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const removeAvatar = () => {
        setUserAvatar(null);
        localStorage.removeItem("neon_user_avatar");
    };

    const connectGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/settings";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="relative pt-16 min-h-screen overflow-hidden" data-testid="settings-page">
            <div className="kana kana-watermark absolute right-2 top-24 text-[8rem] lg:text-[11rem] leading-none pointer-events-none" aria-hidden="true">
                設定
            </div>
            <div
                aria-hidden="true"
                className="hidden lg:block absolute left-[-70px] top-[38%] w-[300px] h-[460px] char-bleed-r bg-cover bg-center opacity-[0.09] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/photo-1761083261633-5aa782b6ddfc?q=80&w=700&auto=format&fit=crop)" }}
            />
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
                    <div className="font-mono2 text-[11px] tracking-[0.3em] text-[#49f5e8] cy-text flex items-center gap-3">
                        {t.settings.kicker}
                        <span className="warning-stripes w-12 h-1.5 opacity-70" />
                    </div>
                    <h1 className="font-display font-bold uppercase tracking-wide text-2xl sm:text-3xl lg:text-4xl mt-3">
                        {t.settings.title}
                    </h1>
                    <p className="text-sm text-slate-500 mt-3 font-mono2 text-[11px] tracking-wider">{t.settings.sub}</p>
                </motion.div>

                <div className="space-y-5 mt-10">
                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6"
                        data-testid="settings-theme"
                    >
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4">
                            <Palette size={13} className="text-[#fcee0a]" />
                            {t.settings.themeLabel}
                        </div>
                        <div className="space-y-2">
                            {["dark", "light", "cyber"].map((id) => (
                                <Row key={id} testid={`settings-theme-${id}`} active={theme === id} dot={THEME_DOTS[id]} label={t.theme[id]} onClick={() => setTheme(id)} />
                            ))}
                        </div>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.18, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6"
                        data-testid="settings-lang"
                    >
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4">
                            <Languages size={13} className="text-[#49f5e8]" />
                            {t.settings.langLabel}
                        </div>
                        <div className="space-y-2">
                            <Row testid="settings-lang-ua" active={lang === "ua"} dot="#d7ff35" label="Українська" onClick={() => setLang("ua")} />
                            <Row testid="settings-lang-en" active={lang === "en"} dot="#49f5e8" label="English" onClick={() => setLang("en")} />
                        </div>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.26, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6"
                        data-testid="settings-voice"
                    >
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4">
                            <AudioLines size={13} className="text-[#ff4bd8]" />
                            {t.settings.voiceLabel}
                        </div>
                        <div className="space-y-2">
                            {["female", "male", "neutral"].map((id) => (
                                <Row key={id} testid={`settings-voice-${id}`} active={voicePref === id} dot={VOICE_DOTS[id]} label={t.settings.voiceOpts[id]} onClick={() => setVoicePref(id)} />
                            ))}
                        </div>
                        <p className="font-mono2 text-[9px] text-slate-600 tracking-wider mt-3">{t.settings.voiceNote}</p>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.34, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6 space-y-5"
                    >
                        <div className="flex items-center justify-between gap-4" data-testid="settings-speak-row">
                            <div className="flex items-start gap-3">
                                <Volume2 size={16} className="text-[#d7ff35] mt-0.5" />
                                <div>
                                    <div className="text-sm">{t.settings.speakLabel}</div>
                                    <div className="text-xs text-slate-500 mt-1">{t.settings.speakDesc}</div>
                                </div>
                            </div>
                            <button
                                data-testid="settings-speak-toggle"
                                onClick={() => setSpeak(!speak)}
                                className={`shrink-0 w-12 h-7 rounded-full border transition-colors relative ${speak ? "bg-[#d7ff35] border-[#d7ff35]" : "bg-[#0b0f1a] border-[#2c3a5c]"}`}
                            >
                                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${speak ? "left-[26px]" : "left-1"}`} />
                            </button>
                        </div>
                        <div className="flex items-start gap-3 border-t border-[#212b45] pt-5">
                            <Mic size={16} className="text-[#ff4bd8] mt-0.5" />
                            <div>
                                <div className="text-sm">{t.settings.micLabel}</div>
                                <div className="text-xs text-slate-500 mt-1">{t.settings.micDesc}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 border-t border-[#212b45] pt-5">
                            <AudioLines size={16} className="text-[#fcee0a] mt-0.5" />
                            <div className="flex-1">
                                <div className="text-sm">{t.settings.bgVoiceLabel}</div>
                                <div className="text-xs text-slate-500 mt-1">{t.settings.bgVoiceDesc}</div>
                                <p className="font-mono2 text-[9px] text-[#fcee0a]/60 tracking-wider mt-2">⚠ {t.settings.bgNote}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 border-t border-[#212b45] pt-5">
                            <Smartphone size={16} className="text-[#49f5e8] mt-0.5" />
                            <div className="flex-1">
                                <div className="text-sm">Керування телефоном у закритому стані</div>
                                <div className="text-xs text-slate-500 mt-1">Фоновий сервіс уже в Android-проєкті: тримає мікрофон активним, чекає «Гей, Kiber», виконує дзвінки/застосунки. Збери APK — і керуй телефоном голосом навіть із заблокованого екрана.</div>
                                <p className="font-mono2 text-[9px] text-[#fcee0a]/60 tracking-wider mt-2">⚠ Повне керування телефоном у закритому стані — лише в Android-застосунку (фоновий сервіс уже в проєкті).</p>
                            </div>
                        </div>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6"
                        data-testid="settings-account"
                    >
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4">
                            <UserCircle2 size={13} className="text-[#49f5e8]" />
                            {t.settings.accountLabel}
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#49f5e8]/50 bg-[#1c0f33] grid place-items-center shrink-0" data-testid="settings-google-avatar">
                                {user ? (
                                    user.picture ? (
                                        <img src={user.picture} alt={user.name || "Google"} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="font-display font-bold text-lg text-[#49f5e8]">{(user.name || user.email || "G")[0].toUpperCase()}</span>
                                    )
                                ) : (
                                    <UserCircle2 size={28} className="text-slate-600" />
                                )}
                            </span>
                            <div className="flex-1">
                                <p className="text-xs text-slate-500 mb-3">{t.settings.accountDesc}</p>
                                {user ? (
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-mono2 text-[10px] text-[#49f5e8] tracking-wider" data-testid="settings-google-email">{user.email}</span>
                                        <button
                                            data-testid="settings-google-disconnect"
                                            onClick={logout}
                                            className="flex items-center gap-2 border border-[#ff4bd8]/50 text-[#ff4bd8] font-mono2 text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-xl hover:bg-[#ff4bd8]/10 transition-colors"
                                        >
                                            <X size={13} />
                                            {t.settings.disconnect}
                                        </button>
                                        <button
                                            data-testid="settings-google-sync"
                                            onClick={syncNow}
                                            disabled={syncing}
                                            className="flex items-center gap-2 border border-[#d7ff35]/50 text-[#d7ff35] font-mono2 text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-xl hover:bg-[#d7ff35]/10 transition-colors disabled:opacity-50"
                                        >
                                            <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
                                            {t.settings.connected} {syncStatus}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        data-testid="settings-google-connect"
                                        onClick={connectGoogle}
                                        className="flex items-center gap-2 border border-[#49f5e8]/60 text-[#49f5e8] font-mono2 text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-xl hover:bg-[#49f5e8]/10 transition-colors"
                                    >
                                        {t.settings.connect}
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6"
                        data-testid="settings-avatar"
                    >
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4">
                            <Upload size={13} className="text-[#49f5e8]" />
                            {t.settings.avatarLabel}
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#49f5e8]/50 bg-[#1c0f33] grid place-items-center shrink-0" data-testid="settings-avatar-preview">
                                {userAvatar ? (
                                    <img src={userAvatar} alt="avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <UserCircle2 size={30} className="text-slate-600" />
                                )}
                            </span>
                            <div className="flex-1">
                                <p className="text-xs text-slate-500 mb-3">{t.settings.avatarDesc}</p>
                                <div className="flex gap-3">
                                    <button
                                        data-testid="settings-avatar-upload"
                                        onClick={() => fileRef.current?.click()}
                                        className="flex items-center gap-2 border border-[#49f5e8]/60 text-[#49f5e8] font-mono2 text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-xl hover:bg-[#49f5e8]/10 transition-colors"
                                    >
                                        <Upload size={13} />
                                        {t.settings.avatarUpload}
                                    </button>
                                    {userAvatar && (
                                        <button
                                            data-testid="settings-avatar-remove"
                                            onClick={removeAvatar}
                                            className="flex items-center gap-2 border border-[#ff4bd8]/50 text-[#ff4bd8] font-mono2 text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-xl hover:bg-[#ff4bd8]/10 transition-colors"
                                        >
                                            <X size={13} />
                                            {t.settings.avatarRemove}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="settings-avatar-input" onChange={onAvatarFile} />
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.46, duration: 0.6 }}
                        className="glow-card rounded-2xl p-6"
                        data-testid="settings-logo"
                    >
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4">
                            <UserCircle2 size={13} className="text-[#fcee0a]" />
                            {t.settings.logoLabel}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { id: "david", name: "ДЕВІД", avatar: "/char/av-david.png", ring: "border-[#7c3aed]" },
                                { id: "lucy", name: lang === "ua" ? "ЛЮСІ" : "LUCY", avatar: "/char/av-lucy.png", ring: "border-[#ff4bd8]" },
                            ].map((o) => (
                                <button
                                    key={o.id}
                                    data-testid={`settings-logo-${o.id}`}
                                    onClick={() => setLogoChar(o.id)}
                                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors ${
                                        logoChar === o.id ? `${o.ring} bg-white/5` : "border-[#212b45] hover:border-[#49f5e8]/40"
                                    }`}
                                >
                                    <span className={`w-11 h-11 rounded-xl overflow-hidden border ${o.ring} bg-[#1c0f33] shrink-0`}>
                                        <img src={o.avatar} alt={o.name} className="w-full h-full object-cover" />
                                    </span>
                                    <span className="font-mono2 text-[10px] tracking-[0.2em]">{o.name}</span>
                                    {logoChar === o.id && <Check size={14} className="ml-auto text-[#fcee0a]" />}
                                </button>
                            ))}
                        </div>
                        <p className="font-mono2 text-[9px] text-slate-600 tracking-wider mt-3">{t.settings.logoNote}</p>
                    </motion.section>

                    <div className="pt-2 pb-4 text-center" data-testid="settings-creator">
                        <span className="font-mono2 text-[10px] tracking-[0.3em] text-slate-600">
                            {lang === "ua" ? "СТВОРИВ" : "CREATED BY"} //{" "}
                            <span className="text-[#fcee0a]/80">PETRO KAMINNIY</span>
                        </span>
                        <div className="warning-stripes w-24 h-1 opacity-50 mx-auto mt-3" />
                    </div>
                </div>
            </div>
        </div>
    );
}
